/**
 * Clip Renderer
 * Handles rendering of clip content in exercise mode
 */

import appState from '../core/app-state.js';
import logger from '../core/logger.js';
import { logEvent } from '../core/event-logger.js';
import { EVENT_TYPES } from '../core/event-types.js';
import * as ClipCoordinator from '../services/clip-coordinator.js';
import * as QuestionManager from '../modules/questions/question-manager.js';
import { renderCurrentQuestion } from '../modules/questions/question-coordinator.js';
import { initCustomPlayer } from '../modules/player/audio-player.js';
import { initializeClipDepthTracking } from '../modules/player/depth-metrics.js';
import * as TranscriptModule from '../modules/player/transcript.js';
import { updateNavControls } from '../modules/player/clip-controller.js';
import type { Clip } from '../types/clip.js';

/**
 * Format a slug/ID to title case
 */
function formatSlugToTitle(slug: string | number): string {
  return String(slug)
    .replace(/[-_]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Create a clip ID info icon with tooltip for display
 * @param clipId - The clip ID to display
 * @returns A span element with info icon, tooltip, and accessibility attributes
 */
function createClipIdInfoIcon(clipId: string): HTMLSpanElement {
  // Create container span
  const infoIcon = document.createElement('span');
  infoIcon.className = 'clip-id-info';
  infoIcon.setAttribute('role', 'button');
  infoIcon.setAttribute('aria-label', `Show clip ID: ${clipId}`);
  infoIcon.setAttribute('tabindex', '0');
  infoIcon.textContent = 'ⓘ';
  
  // Create tooltip
  const tooltip = document.createElement('span');
  tooltip.className = 'clip-id-tooltip';
  tooltip.textContent = clipId;
  tooltip.setAttribute('role', 'tooltip');
  infoIcon.appendChild(tooltip);
  
  // Handle click/tap for touch devices
  let tooltipTimeout: ReturnType<typeof setTimeout> | null = null;
  
  const toggleTooltip = (e: Event) => {
    e.stopPropagation();
    
    // Toggle tooltip visibility
    const isShown = infoIcon.classList.contains('show-tooltip');
    
    if (isShown) {
      infoIcon.classList.remove('show-tooltip');
      if (tooltipTimeout) {
        clearTimeout(tooltipTimeout);
        tooltipTimeout = null;
      }
    } else {
      infoIcon.classList.add('show-tooltip');
      
      // Auto-hide after 3 seconds on touch devices
      tooltipTimeout = setTimeout(() => {
        infoIcon.classList.remove('show-tooltip');
        tooltipTimeout = null;
      }, 3000);
    }
  };
  
  // Add event listeners for touch/click
  infoIcon.addEventListener('click', toggleTooltip);
  infoIcon.addEventListener('touchstart', (e) => {
    e.preventDefault();
    toggleTooltip(e);
  });
  
  // Keyboard support
  infoIcon.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleTooltip(e);
    }
  });
  
  return infoIcon;
}

/**
 * Get series name from clip's storyName field or fallback to formatting storyId
 */
function getSeriesName(clip: Clip): string {
  if ((clip as any).storyName) {
    return (clip as any).storyName as string;
  }
  if (clip.storyId) {
    return formatSlugToTitle(clip.storyId);
  }
  return 'Unknown Series';
}

/**
 * Get series progress string (e.g., "2/5")
 * Returns null if can't determine total parts
 */
function getSeriesProgress(clip: Clip): string | null {
  if (!clip.storyId || !clip.storyPart) {
    return null;
  }
  
  // Find all clips in the same series
  const seriesClips = appState.clips.filter(c => c.storyId === clip.storyId);
  if (seriesClips.length === 0) {
    return null;
  }
  
  // Get max story part to determine total
  const maxPart = Math.max(...seriesClips.map(c => c.storyPart || 0));
  if (maxPart === 0) {
    return null;
  }
  
  return `${clip.storyPart}/${maxPart}`;
}

/**
 * Generate metadata label for story/lesson information
 */
export function generateMetadataLabel(clip: Clip): string | null {
  if (!clip) return null;
  
  const parts: string[] = [];
  
  // Add story information if present
  if (clip.storyId && clip.storyPart) {
    const storyTitle = getSeriesName(clip);
    parts.push(`${storyTitle} • Part ${clip.storyPart}`);
  }
  
  // Return null if no metadata
  if (parts.length === 0) return null;
  
  // Join parts with bullet separator
  return parts.join(' • ');
}

/**
 * Update header actions (wrapper for router)
 */
function updateHeaderActions(): void {
  // This function is imported from router in main.js
  // For now, we'll call it via a callback that will be registered
  if ((window as any)._updateHeaderActions) {
    (window as any)._updateHeaderActions();
  }
}

/**
 * Update progress UI
 */
function updateProgressUI(): void {
  QuestionManager.updateQuestionProgressBar();
}

/**
 * Render a clip by index or clip object (wrapper for backward compatibility)
 */
export function renderClip(idxOrClip: number | Clip): void {
  // If we received an index, convert to clip object
  if (typeof idxOrClip === 'number') {
    const clip = appState.clips[idxOrClip];
    if (!clip) return;
    renderClipContent(clip, idxOrClip);
  } else {
    // We received a clip object directly
    const idx = appState.clips.findIndex(c => c.id === idxOrClip.id);
    renderClipContent(idxOrClip, idx);
  }
}

/**
 * Render a clip's content given a clip object
 */
export function renderClipContent(clip: Clip, idx: number): void {
  // Reset transcript injected flag for the new clip
  appState.transcriptInjected = false;
  
  // Reset question index for new clip
  appState.currentQuestionIndex = 0;
  
  // Clean up event listeners for transcript panel
  TranscriptModule.removeTranscriptEventListeners();
  
  // Log clip_loaded event
  const eventType = EVENT_TYPES['CLIP_LOADED'];
  if (eventType) {
    logEvent(eventType, { clipId: clip.id, title: clip.title });
  }
  
  // Reset transcript scroll for new clip
  if (appState.currentClip) {
    // Only clear the specific clip's scroll if we're changing clips
    if (clip.id !== appState.currentClip.id) {
      appState.transcriptScroll.delete(appState.currentClip.id);
    }
  }
  
  // Update current clip reference
  appState.currentClip = clip;
  appState.activeClipId = clip.id;
  
  // Initialize depth tracking for this clip
  initializeClipDepthTracking(clip.id);
  
  // Clear the feedback region when loading a new clip
  const feedbackRegion = document.getElementById('feedback-region');
  if (feedbackRegion) {
    feedbackRegion.innerHTML = '';
  }
  
  // Update question progress bar
  updateProgressUI();
  
  // Update header actions for exercise mode
  updateHeaderActions();
  
  // Generate metadata label if story/lesson fields are present
  const metadataLabel = generateMetadataLabel(clip);
  
  // Create metadata line if label exists
  let metadataElement: HTMLElement | undefined;
  if (metadataLabel) {
    metadataElement = document.createElement('div');
    metadataElement.className = 'meta-line';
    
    // Add data-story-id attribute if storyId is present
    if (clip.storyId) {
      metadataElement.setAttribute('data-story-id', String(clip.storyId));
    }
    
    metadataElement.innerHTML = `
      <span class="sr-only">Narrative sequence: </span>
      <span>${metadataLabel}</span>
    `;
    
    // Log metadata render event
    const metadataEventType = EVENT_TYPES['CLIP_METADATA_RENDER'];
    if (metadataEventType) {
      logEvent(metadataEventType, {
        clipId: clip.id,
        storyId: clip.storyId,
        storyPart: clip.storyPart,
        lessonId: (clip as any).lessonId,
        lessonPosition: (clip as any).lessonPosition
      });
    }
  }
  
  // Create clip title as h2 in player section
  const clipTitleHeading = document.createElement('h2');
  clipTitleHeading.className = 'clip-title-heading text-lg font-bold mb-3';
  
  // Create SVG icon
  const svgIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svgIcon.setAttribute('fill', 'none');
  svgIcon.setAttribute('viewBox', '0 0 24 24');
  svgIcon.setAttribute('stroke-width', '1.5');
  svgIcon.setAttribute('stroke', 'currentColor');
  svgIcon.setAttribute('class', 'h-5 w-5 mr-1 inline-block align-middle opacity-80');
  
  const svgTitle = document.createElementNS('http://www.w3.org/2000/svg', 'title');
  svgTitle.textContent = 'Audio';
  svgIcon.appendChild(svgTitle);
  
  const svgPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  svgPath.setAttribute('stroke-linecap', 'round');
  svgPath.setAttribute('stroke-linejoin', 'round');
  svgPath.setAttribute('d', 'm9 9 10.5-3m0 6.553v3.75a2.25 2.25 0 0 1-1.632 2.163l-1.32.377a1.803 1.803 0 1 1-.99-3.467l2.31-.66a2.25 2.25 0 0 0 1.632-2.163Zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 0 1-1.632 2.163l-1.32.377a1.803 1.803 0 0 1-.99-3.467l2.31-.66A2.25 2.25 0 0 0 9 15.553Z');
  svgIcon.appendChild(svgPath);
  
  clipTitleHeading.appendChild(svgIcon);
  
  // Add title text
  const titleSpan = document.createElement('span');
  titleSpan.className = 'align-middle';
  titleSpan.textContent = clip.title;
  clipTitleHeading.appendChild(titleSpan);
  
  // Add clip ID info icon
  const clipIdInfo = createClipIdInfoIcon(clip.id);
  clipTitleHeading.appendChild(clipIdInfo);
  
  // Create title row container with badges
  const titleRow = document.createElement('div');
  titleRow.className = 'title-row';
  titleRow.style.cssText = 'display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; flex-wrap: wrap; margin-bottom: 0.75rem;';
  
  // Add title to the left
  titleRow.appendChild(clipTitleHeading);
  clipTitleHeading.style.marginBottom = '0';
  
  // Create badge container for the right side
  const badgeContainer = document.createElement('div');
  badgeContainer.style.cssText = 'display: flex; gap: 0.5rem; flex-wrap: wrap;';
  
  // Series progress badge
  if (clip.storyId && clip.storyPart) {
    const seriesProgress = getSeriesProgress(clip);
    if (seriesProgress) {
      const seriesBadge = document.createElement('span');
      seriesBadge.className = 'clip-badge series-badge';
      seriesBadge.textContent = seriesProgress;
      seriesBadge.style.cssText = 'font-size: 0.75rem; padding: 0.25rem 0.5rem; background-color: #e5e7eb; color: #374151; border-radius: 0.25rem; font-weight: 500;';
      seriesBadge.setAttribute('aria-label', `Series progress: ${seriesProgress}`);
      badgeContainer.appendChild(seriesBadge);
    }
  }
  
  // Difficulty badge
  if (clip.difficulty) {
    const difficultyBadge = document.createElement('span');
    difficultyBadge.className = 'clip-badge difficulty-badge';
    difficultyBadge.textContent = clip.difficulty.toUpperCase();
    difficultyBadge.style.cssText = 'font-size: 0.75rem; padding: 0.25rem 0.5rem; background-color: #f3f4f6; color: #1f2937; border-radius: 0.25rem; font-weight: 600; border: 1px solid #d1d5db;';
    difficultyBadge.setAttribute('aria-label', `Difficulty: ${clip.difficulty}`);
    badgeContainer.appendChild(difficultyBadge);
  }
  
  // Add badge container to title row if it has badges
  if (badgeContainer.children.length > 0) {
    titleRow.appendChild(badgeContainer);
  }

  // Audio section
  const audioSection = document.getElementById('audio-section');
  if (!audioSection) {
    logger.error('audio-section element not found');
    return;
  }
  audioSection.innerHTML = '';

  // Set up custom audio player in player section
  const playerSection = document.getElementById('player-section');
  if (!playerSection) {
    logger.error('player-section element not found');
    return;
  }
  playerSection.innerHTML = '';
  
  // Add metadata element first if it exists
  if (metadataElement) {
    playerSection.appendChild(metadataElement);
  }
  
  // Add title row (with badges) to player section
  playerSection.appendChild(titleRow);
  
  // Add playback progress bar (non-interactive, read-only)
  const progressBarContainer = document.createElement('div');
  progressBarContainer.className = 'playback-progress-container';
  
  const progressBar = document.createElement('div');
  progressBar.id = 'playbackProgressBar';
  progressBar.className = 'playback-progress-bar';
  progressBar.setAttribute('role', 'progressbar');
  progressBar.setAttribute('aria-label', 'Playback progress');
  progressBar.setAttribute('aria-valuemin', '0');
  progressBar.setAttribute('aria-valuemax', '100');
  progressBar.setAttribute('aria-valuenow', '0');
  progressBar.setAttribute('tabindex', '-1');
  
  const progressBarFill = document.createElement('div');
  progressBarFill.id = 'playbackProgressBarFill';
  progressBarFill.className = 'playback-progress-bar-fill';
  progressBarFill.style.width = '0%';
  
  progressBar.appendChild(progressBarFill);
  progressBarContainer.appendChild(progressBar);
  
  // Create time display row (under progress bar)
  const timeRow = document.createElement('div');
  timeRow.className = 'time-row';
  
  // Create elapsed time span
  const elapsedTime = document.createElement('span');
  elapsedTime.id = 'elapsedTime';
  elapsedTime.textContent = '00:00';
  elapsedTime.setAttribute('aria-label', 'Elapsed time');
  
  // Create remaining time span
  const remainingTime = document.createElement('span');
  remainingTime.id = 'remainingTime';
  remainingTime.textContent = '−00:00';
  remainingTime.setAttribute('aria-label', 'Remaining time');
  
  // Assemble time row
  timeRow.appendChild(elapsedTime);
  timeRow.appendChild(remainingTime);
  
  // Add time row to progress bar container
  progressBarContainer.appendChild(timeRow);
  
  playerSection.appendChild(progressBarContainer);
  
  // Log progress bar render event
  const progressEventType = EVENT_TYPES['UI_PROGRESSBAR_RENDER'];
  if (progressEventType) {
    logEvent(progressEventType, {
      clipId: clip.id
    });
  }

  // Create custom audio wrapper
  const customAudioWrapper = document.createElement('div');
  customAudioWrapper.className = 'custom-audio-wrapper';
  customAudioWrapper.tabIndex = -1;
  
  // Create audio element (without controls)
  const audio = document.createElement('audio');
  audio.src = clip.audioFile;
  audio.className = 'w-full';
  
  // Create audio controls container
  const audioControls = document.createElement('div');
  audioControls.className = 'audio-controls';
  audioControls.setAttribute('role', 'group');
  audioControls.setAttribute('aria-label', 'Audio controls');
  
  // Create player controls row with three-zone layout
  const playerControlsRow = document.createElement('div');
  playerControlsRow.className = 'player-controls-row';
  
  // Left group container
  const leftGroup = document.createElement('div');
  leftGroup.className = 'player-controls-left';
  
  // Center group container
  const centerGroup = document.createElement('div');
  centerGroup.className = 'player-controls-center';
  
  // Right group container
  const rightGroup = document.createElement('div');
  rightGroup.className = 'player-controls-right';
  
  // Create rewind button (icon-only)
  const rewindBtn = document.createElement('button');
  rewindBtn.id = 'rewindBtn';
  rewindBtn.className = 'player-btn icon-only';
  rewindBtn.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="player-icon">
      <path d="M9.195 18.44c1.25.713 2.805-.19 2.805-1.629v-2.34l6.945 3.968c1.25.714 2.805-.188 2.805-1.628V8.688c0-1.44-1.555-2.342-2.805-1.628L12 11.03v-2.34c0-1.44-1.555-2.343-2.805-1.629l-7.108 4.062c-1.26.72-1.26 2.536 0 3.256l7.108 4.061z" />
    </svg>
    <span style="position: absolute; bottom: 2px; font-size: 10px; font-weight: 600;">10s</span>
  `;
  rewindBtn.setAttribute('aria-label', 'Rewind 10 seconds');
  rewindBtn.setAttribute('title', 'Rewind 10 seconds');
  
  // Create play/pause button
  const playPauseBtn = document.createElement('button');
  playPauseBtn.id = 'playPauseBtn';
  playPauseBtn.className = 'player-btn primary';
  playPauseBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="player-icon">
    <path fill-rule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.715 1.295 2.567 0 3.283L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653z" clip-rule="evenodd" />
  </svg>`;
  playPauseBtn.setAttribute('aria-label', 'Play');
  playPauseBtn.setAttribute('aria-pressed', 'false');
  
  // Create forward button (icon-only)
  const forwardBtn = document.createElement('button');
  forwardBtn.id = 'forwardBtn';
  forwardBtn.className = 'player-btn icon-only';
  forwardBtn.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="player-icon">
      <path d="M5.055 7.06c-1.25-.714-2.805.189-2.805 1.628v8.123c0 1.44 1.555 2.342 2.805 1.628L12 14.471v2.34c0 1.44 1.555 2.342 2.805 1.628l7.108-4.061c1.26-.72 1.26-2.536 0-3.256l-7.108-4.061C13.555 6.346 12 7.25 12 8.688v2.34L5.055 7.06z" />
    </svg>
    <span style="position: absolute; bottom: 2px; font-size: 10px; font-weight: 600;">10s</span>
  `;
  forwardBtn.setAttribute('aria-label', 'Forward 10 seconds');
  forwardBtn.setAttribute('title', 'Forward 10 seconds');
  
  // Assemble the control groups
  leftGroup.appendChild(rewindBtn);
  centerGroup.appendChild(playPauseBtn);
  rightGroup.appendChild(forwardBtn);
  
  playerControlsRow.appendChild(leftGroup);
  playerControlsRow.appendChild(centerGroup);
  playerControlsRow.appendChild(rightGroup);
  
  // Assemble the audio controls
  audioControls.appendChild(playerControlsRow);
  
  customAudioWrapper.appendChild(audio);
  customAudioWrapper.appendChild(audioControls);
  
  playerSection.appendChild(customAudioWrapper);
  
  // Initialize the custom player
  initCustomPlayer(audio, customAudioWrapper);

  // Update progress bar and navigation
  updateProgressUI();
  updateNavControls();

  // Create a container for questions
  const questionContainer = document.createElement('div');
  questionContainer.id = 'questionContainer';
  questionContainer.className = 'question-container';
  audioSection.appendChild(questionContainer);

  // Handle questions for this clip
  if (clip.questions && clip.questions.length > 0) {
    renderCurrentQuestion(clip, idx);
  } else {
    // No questions for this clip
    const noQuestionsMessage = document.createElement('p');
    noQuestionsMessage.textContent = 'No questions for this clip.';
    noQuestionsMessage.className = 'text-center text-gray-500 py-4';
    questionContainer.appendChild(noQuestionsMessage);
    
    // For zero-question clips, mark as completed immediately
    ClipCoordinator.markClipCompleted(clip.id);
    
    // Update UI elements
    updateProgressUI();
    updateNavControls();
  }
  
  // Setup transcript gating based on audio play
  TranscriptModule.setupTranscriptGating(audio, clip, audioSection);
}
